import { Signal, effect, untracked, useComputed } from "@suse-o11y/signal";
import { useMemo } from "@suse-o11y/use-memo";
import { usePermanent } from "@suse-o11y/use-permanent";
import React from "react";
import { useSelector } from "react-redux";

import { selectTimeRange } from "../../../app/state/view/timeline/TimelineSelectors";
import { LAST_1_HOUR as DEFAULT_TIME_RANGE, type SpecificRange } from "../../../app/state/view/timeline/TimeRange";
import { useNavigateFrom } from "../../../components/react/link";

import { DashboardContextProvider, type DashboardContextValue } from "./_dashboard-context";
import { DashboardState } from "./_dashboard-state";
import { useDashboardVariables } from "./_use-dashboard-variables";
import { DashboardTemplateVariablesBoundary } from "./template-variables-boundary";

export const DashboardProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const navigateFrom = useNavigateFrom();
	const timeRangeFromUrl = useSelector(selectTimeRange) ?? DEFAULT_TIME_RANGE;
	const state = usePermanent(() => new DashboardState());
	const [dashboardVariables, setDashboardVariables] = useDashboardVariables(state);
	const chartTimeRange = usePermanent(() =>
		Signal<SpecificRange>(timeRangeFromUrl.toSpecific(), {
			equals: (left, right) => left.isEqual(right),
		}),
	);

	const timeRange = React.useMemo(
		() =>
			Signal(
				(monitor) => state.form.fields.settings.fields.timeRange.getInput(monitor) ?? timeRangeFromUrl,
				(next, monitor) => {
					if (state.form.fields.settings.fields.timeRange.getInput(monitor) == null) {
						navigateFrom("*", {
							search: (params) => ({
								...params,
								timestamp: next.isSpecific() ? params.timestamp : undefined,
								timeRange: next,
							}),
						});
					} else {
						state.form.fields.settings.fields.timeRange.setInput(next);
					}
				},
				{
					equals: (left, right) => left.isEqual(right),
				},
			),
		[navigateFrom, state, timeRangeFromUrl],
	);

	React.useLayoutEffect(
		() =>
			effect((monitor) => {
				chartTimeRange.write(timeRange.read(monitor).toSpecific());
			}),
		[chartTimeRange, timeRange],
	);

	const refreshChartTimeRange = React.useCallback(() => {
		untracked((monitor) => {
			chartTimeRange.write(timeRange.read(monitor).toSpecific());
		});
	}, [chartTimeRange, timeRange]);

	const variablesSignal = useMemo(
		() => Signal(() => dashboardVariables, setDashboardVariables),
		[dashboardVariables, setDashboardVariables],
	);

	const context = useMemo(
		(): DashboardContextValue => ({
			timeRange,
			chartTimeRange,
			refreshChartTimeRange,
			state,
			variables: variablesSignal,
		}),
		[timeRange, chartTimeRange, refreshChartTimeRange, state, variablesSignal],
	);

	return (
		<DashboardContextProvider value={context}>
			<DashboardTemplateVariablesBoundary timeRange={useComputed(chartTimeRange)} variables={dashboardVariables}>
				{children}
			</DashboardTemplateVariablesBoundary>
		</DashboardContextProvider>
	);
};
